import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Check, 
  ArrowRight, 
  MapPin,
  Hotel,
  Plane,
  TrendingUp,
  Users,
  Eye,
  MessageCircle,
  IndianRupee,
  Calendar,
  Star,
  Play,
  Sparkles,
  Target,
  BarChart3,
  Phone,
  Building2,
  Mountain,
  Palmtree
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { cubeLogo, landscapeVideos, shortsVideos } from "../data/mock";

// Case Study Videos with Dashboard Metrics - Using your actual ad videos
const caseStudies = [
  {
    id: "aqua-kutir",
    name: "Aqua Kutir",
    location: "Raichak, West Bengal",
    type: "Premium Resort",
    youtubeId: "x_VW1wezL70",
    metrics: {
      reach: "2.8L+",
      engagement: "45K+",
      enquiries: "380+",
      bookings: "95+",
      spent: "₹18,000",
      duration: "30 Days"
    },
    highlight: "4.2x ROAS"
  },
  {
    id: "zuluk-homestay",
    name: "Zuluk Homestay",
    location: "Zuluk, Sikkim",
    type: "Mountain Homestay",
    youtubeId: "yTQFRj0pLKk",
    metrics: {
      reach: "1.5L+",
      engagement: "28K+",
      enquiries: "220+",
      bookings: "65+",
      spent: "₹12,000",
      duration: "25 Days"
    },
    highlight: "3.8x ROAS"
  },
  {
    id: "silkroute-tour",
    name: "Silkroute Tour",
    location: "Old Silk Route, Sikkim",
    type: "Travel Agency",
    youtubeId: "0JHTYaB954o",
    metrics: {
      reach: "3.2L+",
      engagement: "52K+",
      enquiries: "450+",
      bookings: "120+",
      spent: "₹25,000",
      duration: "30 Days"
    },
    highlight: "5.1x ROAS"
  },
  {
    id: "gangani-guesthouse",
    name: "Garbeta Gangani Appayan",
    location: "Garbeta, West Bengal",
    type: "Guest House",
    youtubeId: "W09Dre_tBE4",
    metrics: {
      reach: "1.2L+",
      engagement: "22K+",
      enquiries: "180+",
      bookings: "48+",
      spent: "₹10,000",
      duration: "20 Days"
    },
    highlight: "3.5x ROAS"
  }
];

// Ad Creatives Gallery (User uploaded images)
const adCreatives = [
  {
    id: "creative-1",
    image: "https://customer-assets.emergentagent.com/job_2e3e41cb-8232-4fca-af7d-2026206249af/artifacts/ql9cxc6l_499735125_122165266568537635_355163612412716750_n.jpg",
    client: "Doll's Heaven",
    campaign: "Sikkim Group Tour",
    type: "Facebook Ad"
  },
  {
    id: "creative-2",
    image: "https://customer-assets.emergentagent.com/job_2e3e41cb-8232-4fca-af7d-2026206249af/artifacts/zhmg2t0q_499963847_122165263496537635_1847578294807346881_n.jpg",
    client: "Doll's Heaven",
    campaign: "Lungchok Agamlok Tour",
    type: "Facebook Ad"
  },
  {
    id: "creative-3",
    image: "https://customer-assets.emergentagent.com/job_2e3e41cb-8232-4fca-af7d-2026206249af/artifacts/eci1e7tm_500876287_122166297944537635_2874854389837642246_n.jpg",
    client: "Doll's Heaven",
    campaign: "Pahar er Deshe Tour",
    type: "Facebook Ad"
  },
  {
    id: "creative-4",
    image: "https://customer-assets.emergentagent.com/job_2e3e41cb-8232-4fca-af7d-2026206249af/artifacts/cehu34s8_502462173_122166666098537635_7381054442448726088_n.jpg",
    client: "Doll's Heaven",
    campaign: "Sikkim Monastery Tour",
    type: "Facebook Ad"
  }
];

// Popular Destinations We Serve
const destinations = [
  { name: "Darjeeling", icon: Mountain, color: "text-green-400" },
  { name: "Sikkim", icon: Mountain, color: "text-blue-400" },
  { name: "Gangtok", icon: Building2, color: "text-purple-400" },
  { name: "Kerala", icon: Palmtree, color: "text-emerald-400" },
  { name: "North Bengal", icon: Mountain, color: "text-cyan-400" },
  { name: "Digha", icon: Palmtree, color: "text-yellow-400" },
  { name: "Mandarmani", icon: Palmtree, color: "text-orange-400" },
  { name: "Sundarbans", icon: Palmtree, color: "text-lime-400" },
  { name: "Puri", icon: Palmtree, color: "text-amber-400" },
  { name: "Goa", icon: Palmtree, color: "text-pink-400" }
];

// Famous Travel & Hospitality Brands We've Worked With
const trustedBrands = [
  { name: "MakeMyTrip", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/MakeMyTrip_Logo.svg/220px-MakeMyTrip_Logo.svg.png" },
  { name: "Goibibo", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8a/Goibibo_Logo.svg/220px-Goibibo_Logo.svg.png" },
  { name: "OYO Rooms", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1f/Oyo_Rooms_%28logo%29.png/220px-Oyo_Rooms_%28logo%29.png" },
  { name: "Zostel", logo: "https://cdn.worldvectorlogo.com/logos/zostel.svg" },
  { name: "Treebo", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0c/Treebo_hotels_Logo.png/220px-Treebo_hotels_Logo.png" },
  { name: "FabHotels", logo: "https://asset.brandfetch.io/id8KqlmESI/idSoEuPQ8X.png" },
  { name: "Yatra", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/Yatra_Logo.svg/220px-Yatra_Logo.svg.png" },
  { name: "Cleartrip", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/89/Cleartrip_Logo.svg/220px-Cleartrip_Logo.svg.png" }
];

// Services We Offer
const services = [
  {
    icon: Target,
    title: "Facebook & Instagram Ads",
    description: "Targeted campaigns to reach travelers searching for their next destination"
  },
  {
    icon: BarChart3,
    title: "Google Ads & SEO",
    description: "Rank higher when tourists search for homestays and hotels in your area"
  },
  {
    icon: Users,
    title: "Social Media Management",
    description: "Build trust with stunning visuals and engaging content about your property"
  },
  {
    icon: TrendingUp,
    title: "Sales Funnel Strategy",
    description: "Convert enquiries to bookings with our proven lead nurturing system"
  }
];

// Stats
const stats = [
  { value: "50+", label: "Travel Partners", icon: Plane },
  { value: "₹2Cr+", label: "Bookings Generated", icon: IndianRupee },
  { value: "15L+", label: "Travelers Reached", icon: Eye },
  { value: "4.2x", label: "Average ROAS", icon: TrendingUp }
];

const WHATSAPP_URL = "https://wa.me/918017491858?text=Hi%2C%20I%20run%20a%20travel%20business%20and%20I%27m%20interested%20in%20your%20marketing%20services";

// Video Card Component with Dashboard Style
const CaseStudyCard = ({ study, index }) => {
  const [playing, setPlaying] = useState(false);
  const [activeMetric, setActiveMetric] = useState("overview");
  const thumbUrl = `https://img.youtube.com/vi/${study.youtubeId}/0.jpg`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="bg-[#141414] rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 hover:border-red-500/30 transition-all"
    >
      {/* Video Section */}
      <div className="relative aspect-[9/16] max-h-[280px] sm:max-h-[400px] bg-black">
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${study.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
            title={study.name}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        ) : (
          <div 
            className="relative w-full h-full cursor-pointer group"
            onClick={() => setPlaying(true)}
          >
            <img
              src={thumbUrl}
              alt={study.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all" />
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="w-10 h-10 sm:w-16 sm:h-16 rounded-full bg-[#dc2626] flex items-center justify-center shadow-lg"
              >
                <Play className="w-4 h-4 sm:w-7 sm:h-7 text-white fill-white ml-0.5 sm:ml-1" />
              </motion.div>
            </div>
            {/* Property Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-4 bg-gradient-to-t from-black/90 to-transparent">
              <h3 className="font-bold text-xs sm:text-lg line-clamp-1">{study.name}</h3>
              <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-sm text-gray-300">
                <MapPin className="w-2 h-2 sm:w-3 sm:h-3" />
                <span className="line-clamp-1">{study.location}</span>
              </div>
              <span className="hidden sm:inline-block mt-2 px-2 py-1 bg-white/10 rounded text-xs">
                {study.type}
              </span>
            </div>
            {/* ROAS Badge */}
            <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
              <span className="px-2 py-1 sm:px-3 sm:py-1.5 bg-green-500 text-white text-[10px] sm:text-sm font-bold rounded-lg">
                {study.highlight}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Dashboard Metrics Section */}
      <div className="p-2 sm:p-4">
        {/* Metric Tabs */}
        <div className="flex gap-0.5 sm:gap-1 mb-2 sm:mb-4 p-0.5 sm:p-1 bg-white/5 rounded-lg overflow-x-auto">
          {["overview", "reach", "engagement", "bookings"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveMetric(tab)}
              className={`px-1.5 sm:px-3 py-1 sm:py-1.5 rounded text-[9px] sm:text-xs font-medium capitalize whitespace-nowrap transition-all ${
                activeMetric === tab
                  ? "bg-[#dc2626] text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {tab === "overview" ? "Info" : tab}
            </button>
          ))}
        </div>

        {/* Metrics Display */}
        {activeMetric === "overview" && (
          <div className="grid grid-cols-3 gap-1 sm:gap-3">
            <div className="text-center p-1.5 sm:p-3 bg-white/5 rounded-lg">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4 mx-auto text-blue-400 mb-0.5 sm:mb-1" />
              <p className="text-xs sm:text-lg font-bold">{study.metrics.reach}</p>
              <p className="text-[8px] sm:text-[10px] text-gray-400">Reach</p>
            </div>
            <div className="text-center p-1.5 sm:p-3 bg-white/5 rounded-lg">
              <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4 mx-auto text-green-400 mb-0.5 sm:mb-1" />
              <p className="text-xs sm:text-lg font-bold">{study.metrics.enquiries}</p>
              <p className="text-[8px] sm:text-[10px] text-gray-400">Enquiries</p>
            </div>
            <div className="text-center p-1.5 sm:p-3 bg-white/5 rounded-lg">
              <Hotel className="w-3 h-3 sm:w-4 sm:h-4 mx-auto text-red-400 mb-0.5 sm:mb-1" />
              <p className="text-xs sm:text-lg font-bold">{study.metrics.bookings}</p>
              <p className="text-[8px] sm:text-[10px] text-gray-400">Bookings</p>
            </div>
          </div>
        )}

        {activeMetric === "reach" && (
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-sm text-gray-400">Total Reach</span>
              <span className="font-bold text-blue-400">{study.metrics.reach}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-sm text-gray-400">Amount Spent</span>
              <span className="font-bold text-yellow-400">{study.metrics.spent}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-sm text-gray-400">Duration</span>
              <span className="font-bold">{study.metrics.duration}</span>
            </div>
          </div>
        )}

        {activeMetric === "engagement" && (
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-sm text-gray-400">Engagement</span>
              <span className="font-bold text-green-400">{study.metrics.engagement}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-sm text-gray-400">Enquiries</span>
              <span className="font-bold text-purple-400">{study.metrics.enquiries}</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full" style={{ width: '78%' }} />
            </div>
            <p className="text-xs text-gray-500 text-center">78% Engagement Rate</p>
          </div>
        )}

        {activeMetric === "bookings" && (
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-green-500/10 rounded-lg border border-green-500/20">
              <span className="text-sm text-gray-400">Confirmed Bookings</span>
              <span className="font-bold text-green-400">{study.metrics.bookings}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
              <span className="text-sm text-gray-400">Cost Per Booking</span>
              <span className="font-bold">₹{Math.round(parseInt(study.metrics.spent.replace(/[₹,]/g, '')) / parseInt(study.metrics.bookings))}</span>
            </div>
            <div className="text-center p-3 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg border border-green-500/30">
              <p className="text-2xl font-bold text-green-400">{study.highlight}</p>
              <p className="text-xs text-gray-400">Return on Ad Spend</p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Video Testimonial Component (Landscape)
const VideoTestimonial = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${video.youtubeId}/maxresdefault.jpg`;
  const embedParams = "rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&controls=1&fs=1&cc_load_policy=0&disablekb=0&autoplay=1";

  if (playing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.1 }}
        className="relative aspect-video overflow-hidden rounded-xl bg-black border border-white/10"
      >
        <iframe
          src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&${embedParams}`}
          title={video.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full absolute inset-0"
          loading="lazy"
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="relative aspect-video overflow-hidden rounded-xl cursor-pointer group bg-black border border-white/10 hover:border-yellow-500/30 transition-all"
      onClick={() => setPlaying(true)}
    >
      <img
        src={thumbUrl}
        alt={video.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/20" />
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-16 h-16 rounded-full bg-[#dc2626] flex items-center justify-center shadow-lg shadow-[#dc2626]/30"
        >
          <Play className="w-7 h-7 text-white fill-white ml-1" />
        </motion.div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <p className="text-white font-medium text-sm line-clamp-2">{video.title}</p>
      </div>
    </motion.div>
  );
};

// Shorts Video Testimonial Component (Portrait)
const ShortsVideoTestimonial = ({ video, index }) => {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${video.youtubeId}/0.jpg`;
  const embedParams = "rel=0&modestbranding=1&showinfo=0&iv_load_policy=3&controls=1&fs=1&cc_load_policy=0&disablekb=0&autoplay=1";

  if (playing) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.05 }}
        className="relative aspect-[9/16] overflow-hidden rounded-xl bg-black border border-white/10"
      >
        <iframe
          src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&${embedParams}`}
          title={video.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full absolute inset-0"
          loading="lazy"
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="relative aspect-[9/16] overflow-hidden rounded-xl cursor-pointer group bg-black border border-white/10 hover:border-yellow-500/30 transition-all"
      onClick={() => setPlaying(true)}
    >
      <img
        src={thumbUrl}
        alt={video.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className="w-12 h-12 rounded-full bg-[#dc2626] flex items-center justify-center shadow-lg shadow-[#dc2626]/30"
        >
          <Play className="w-5 h-5 text-white fill-white ml-0.5" />
        </motion.div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-white font-medium text-xs line-clamp-2">{video.title}</p>
        <p className="text-yellow-400 text-[10px] mt-1">{video.industry}</p>
      </div>
    </motion.div>
  );
};

export default function TravelHospitalityPage() {
  const [videoTab, setVideoTab] = useState("landscape");
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleWhatsApp = () => {
    window.open(WHATSAPP_URL, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={cubeLogo} alt="Cube Multimedia" className="h-12 md:h-16" />
          </Link>
          <Button 
            onClick={handleWhatsApp}
            className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
            data-testid="header-cta-btn"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">Talk to Expert</span>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/20 via-transparent to-transparent" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm mb-6">
              <Hotel className="w-4 h-4" />
              Travel & Hospitality Marketing Experts
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Fill Your Rooms with
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400"> More Bookings</span>
            </h1>
            
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
              We've helped 50+ homestays, hotels & travel agencies across India generate massive bookings through strategic digital marketing
            </p>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-3xl mx-auto mb-8 sm:mb-10">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="p-3 sm:p-4 bg-white/5 rounded-xl border border-white/10"
                >
                  <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 mx-auto mb-1 sm:mb-2 text-emerald-400" />
                  <p className="text-xl sm:text-2xl font-bold">{stat.value}</p>
                  <p className="text-[10px] sm:text-xs text-gray-400">{stat.label}</p>
                </motion.div>
              ))}
            </div>

            <Button
              onClick={handleWhatsApp}
              className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white px-6 sm:px-8 py-4 sm:py-6 rounded-xl font-semibold text-sm sm:text-lg flex items-center gap-2 mx-auto"
              data-testid="hero-cta-btn"
            >
              <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Connect with Marketing Expert</span>
              <span className="sm:hidden">Talk to Expert</span>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Destinations We Serve */}
      <section className="px-4 py-8 sm:py-12 border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-gray-400 text-sm sm:text-base mb-4 sm:mb-6">Popular Destinations We Serve</p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {destinations.map((dest, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/5 rounded-full border border-white/10"
              >
                <dest.icon className={`w-3 h-3 sm:w-4 sm:h-4 ${dest.color}`} />
                <span className="text-xs sm:text-sm">{dest.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs mb-4">
                <Sparkles className="w-3 h-3" />
                Real Results
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Our <span className="text-[#dc2626]">Success Stories</span>
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                See how we've transformed bookings for travel businesses across India with data-driven ad campaigns
              </p>
            </motion.div>
          </div>

          {/* Case Study Cards Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {caseStudies.map((study, index) => (
              <CaseStudyCard key={study.id} study={study} index={index} />
            ))}
          </div>

          {/* CTA Below Case Studies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-8 sm:mt-12"
          >
            <p className="text-gray-400 text-sm sm:text-base mb-4">Want similar results for your property?</p>
            <Button
              onClick={handleWhatsApp}
              className="bg-[#dc2626] hover:bg-[#b91c1c] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-base flex items-center gap-2 mx-auto"
            >
              <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              Get Free Strategy Call
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Ad Creatives Gallery */}
      <section className="px-4 py-16 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs mb-4">
                <Sparkles className="w-3 h-3" />
                Our Ad Creatives
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Stunning <span className="text-purple-400">Ad Designs</span> That Convert
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                See the actual ad creatives we've designed for travel businesses that generated massive enquiries
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {adCreatives.map((creative, index) => (
              <motion.div
                key={creative.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/30 transition-all"
              >
                <img
                  src={creative.image}
                  alt={creative.campaign}
                  className="w-full aspect-[4/5] sm:aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                    <p className="text-white font-semibold text-xs sm:text-sm">{creative.client}</p>
                    <p className="text-gray-300 text-[10px] sm:text-xs">{creative.campaign}</p>
                    <span className="inline-block mt-1 sm:mt-2 px-2 py-0.5 sm:py-1 bg-purple-500/20 rounded text-[10px] sm:text-xs text-purple-300">
                      {creative.type}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Feedback Section - with Tabs like Homepage */}
      <section className="px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs mb-4">
                <Star className="w-3 h-3 fill-yellow-400" />
                Client Testimonials
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Hear From Our <span className="text-yellow-400">Happy Clients</span>
              </h2>
              <p className="text-gray-400">Real video feedback from business owners we've helped grow</p>
            </motion.div>
          </div>

          {/* Video Tabs */}
          <div className="flex justify-center gap-2 mb-8">
            <button
              onClick={() => setVideoTab("landscape")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                videoTab === "landscape"
                  ? "bg-[#dc2626] text-white"
                  : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/10"
              }`}
              data-testid="travel-video-tab-landscape"
            >
              Video Feedback
            </button>
            <button
              onClick={() => setVideoTab("shorts")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                videoTab === "shorts"
                  ? "bg-[#dc2626] text-white"
                  : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/10"
              }`}
              data-testid="travel-video-tab-shorts"
            >
              Shorts
            </button>
          </div>

          {/* Landscape Videos */}
          {videoTab === "landscape" && (
            <div className="grid md:grid-cols-2 gap-6">
              {landscapeVideos.filter(v => v.type === "feedback").map((video, index) => (
                <VideoTestimonial key={video.id} video={video} index={index} />
              ))}
            </div>
          )}

          {/* Shorts Videos */}
          {videoTab === "shorts" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {shortsVideos.slice(0, 10).map((video, index) => (
                <ShortsVideoTestimonial key={video.id} video={video} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trusted Brands Section */}
      <section className="px-4 py-12 sm:py-16 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 px-2">
                We've Worked With <span className="text-[#dc2626]">India's Top Travel Brands</span>
              </h2>
              <p className="text-gray-400 text-sm sm:text-base">Trusted by leading hospitality and travel companies</p>
            </motion.div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6">
            {trustedBrands.map((brand, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center justify-center p-4 sm:p-6 bg-white/5 rounded-xl border border-white/10 hover:border-red-500/30 transition-all h-16 sm:h-24"
              >
                <p className="text-sm sm:text-lg font-semibold text-white text-center">{brand.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="px-4 py-16 bg-gradient-to-b from-[#0d0d0d] to-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              How We <span className="text-emerald-400">Grow Your Business</span>
            </h2>
            <p className="text-gray-400">Our proven marketing strategies for travel & hospitality</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-[#141414] rounded-xl border border-white/10 hover:border-emerald-500/30 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="font-bold mb-2">{service.title}</h3>
                <p className="text-sm text-gray-400">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sales Funnel Section */}
      <section className="px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Our <span className="text-cyan-400">Booking Funnel</span> Strategy
            </h2>
            <p className="text-gray-400">How we convert scrollers into guests</p>
          </div>

          <div className="space-y-4">
            {[
              { step: 1, title: "Eye-Catching Video Ads", desc: "We create stunning video content showcasing your property to attract travelers", color: "from-red-500 to-orange-500" },
              { step: 2, title: "Targeted Audience Reach", desc: "We reach people actively searching for stays in your location through precise targeting", color: "from-orange-500 to-yellow-500" },
              { step: 3, title: "Enquiry Generation", desc: "Compelling CTAs drive interested travelers to message you directly on WhatsApp", color: "from-yellow-500 to-green-500" },
              { step: 4, title: "Booking Conversion", desc: "Follow-up sequences and retargeting turn enquiries into confirmed bookings", color: "from-green-500 to-emerald-500" }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4 p-5 bg-[#141414] rounded-xl border border-white/10"
              >
                <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${item.color} flex items-center justify-center shrink-0 font-bold`}>
                  {item.step}
                </div>
                <div>
                  <h3 className="font-bold mb-1">{item.title}</h3>
                  <p className="text-sm text-gray-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="px-4 py-16 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Why <span className="text-[#dc2626]">Travel Businesses</span> Choose Us
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: MapPin, title: "Local Market Expertise", desc: "Deep understanding of tourism trends in North Bengal, Sikkim, Kerala & more" },
              { icon: Star, title: "Proven Track Record", desc: "50+ travel partners with consistent booking growth" },
              { icon: IndianRupee, title: "Budget-Friendly Packages", desc: "Get started with as low as ₹10,000/month" },
              { icon: Calendar, title: "Seasonal Campaigns", desc: "Strategic campaigns during peak tourist seasons" },
              { icon: Users, title: "Dedicated Support", desc: "Personal account manager for your property" },
              { icon: TrendingUp, title: "Transparent Reporting", desc: "Real-time dashboard access to track your results" }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-4 p-5 bg-[#141414] rounded-xl border border-white/10"
              >
                <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center shrink-0">
                  <item.icon className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-gray-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="px-4 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-emerald-900/30 to-cyan-900/30 rounded-2xl p-8 md:p-12 border border-emerald-500/20"
          >
            <Hotel className="w-12 h-12 mx-auto mb-6 text-emerald-400" />
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Fill Your Rooms?
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Let's discuss how we can help your homestay, hotel, or travel agency get more bookings through digital marketing
            </p>
            <Button
              onClick={handleWhatsApp}
              className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white px-10 py-6 rounded-xl font-bold text-lg flex items-center gap-3 mx-auto"
              data-testid="final-cta-btn"
            >
              <MessageCircle className="w-6 h-6" />
              Connect with Marketing Expert
              <ArrowRight className="w-5 h-5" />
            </Button>
            <p className="text-sm text-gray-500 mt-4">Free consultation • No obligation</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={cubeLogo} alt="Cube Multimedia" className="h-10" />
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms-and-conditions" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/refund-policy" className="hover:text-white transition-colors">Refund Policy</Link>
            </div>
          </div>
          <div className="mt-4 text-center text-xs text-gray-500">
            © 2026 Cube Multimedia • www.cubemultimedia.in
          </div>
        </div>
      </footer>
    </div>
  );
}
